import React from 'react'
import cx from 'classnames'
import debounce from 'lodash.debounce'

import reducer, { EActions, initialState, TInitialState } from './reducer'
import { isRedirectToPdpCandidate } from './utils'
import ClientApi from '../../../../api/clientApi'
import { buildUrlFromSuggestion, EPathSegment, useOutsideClick } from '../../../../utils'
import { trackSearchResultEvent, trackSearchSentEvent } from '../../../../utils/gtm'
import AppContext, { TAppContext } from '../../../../components/AppContext/AppContext'

import { TSuggestElement } from '../../../../types/factFinder'

import Icon, { EIconId } from '../../../../components/icons'

const LOWER_LIMIT_BEFORE_SEARCH = 2

const SearchBar: React.FC = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const searchBarRef = React.useRef<HTMLDivElement>(null)
  const suggestionRefs: HTMLLIElement[] = []
  const { locale } = React.useContext<TAppContext>(AppContext)

  React.useEffect(() => {
    updateSuggestions(state.searchTerm).then(() => null)
  }, [state.searchTerm])

  useOutsideClick(searchBarRef, () => dispatch({ type: EActions.CLEAR_SUGGESTIONS }), state.suggestions.length > 0)

  const handleOnChange = debounce((searchValue) => {
    dispatch({ type: EActions.SET_SEARCH_TERM, payload: searchValue })
  }, 200)

  const updateSuggestions = async (value: string) => {
    if (value.length > LOWER_LIMIT_BEFORE_SEARCH) {
      let result
      dispatch({ type: EActions.LOAD_SUGGESTIONS_START })
      try {
        const response = await ClientApi.getSuggestions(value, locale)
        result = response.data
      } catch (e) {
        result = []
      }
      dispatch({ type: EActions.LOAD_SUGGESTIONS_END, payload: result })
    } else {
      if (state.suggestions.length > 0) {
        dispatch({ type: EActions.CLEAR_SUGGESTIONS })
      }
    }
  }

  const handleKeyDownEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { suggestions, selectedSuggestion }: TInitialState = state
    const suggestionsCount = suggestions.length
    if (suggestionsCount > 0) {
      switch (e.key) {
        case 'ArrowUp': {
          const payload: number = selectedSuggestion === -1 ? suggestionsCount - 1 : selectedSuggestion - 1
          dispatch({ type: EActions.SELECT_SUGGESTION, payload })
          break
        }
        case 'ArrowDown': {
          const payload: number = selectedSuggestion === suggestionsCount - 1 ? -1 : selectedSuggestion + 1
          dispatch({ type: EActions.SELECT_SUGGESTION, payload })
          break
        }
        case 'Enter': {
          if (selectedSuggestion > -1) {
            e.preventDefault()
            handleSelectSuggestion(selectedSuggestion)
          }
          break
        }
      }
    }
  }

  const handleSelectSuggestion = (index: number) => {
    const ref = suggestionRefs[index]
    ref && ref.click()
  }

  const handleOnClickSuggestion = (suggestion: TSuggestElement) => {
    if (isRedirectToPdpCandidate(state.searchTerm, state.suggestions)) {
      trackSearchResultEvent({
        searchTermTyped: state.searchTerm,
        searchTermSent: suggestion.name,
      })
    }
    trackSearchSentEvent({
      searchTerm: state.searchTerm,
      suggestion,
      selectedSuggestion: state.suggestions.indexOf(suggestion),
      totalSuggestions: state.suggestions.length,
    })
  }

  const handleOnSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    if (state.isLoading) {
      e.nativeEvent.preventDefault()
      return
    }
    if (isRedirectToPdpCandidate(state.searchTerm, state.suggestions)) {
      // check :: searchTermSent === typed in search term
      trackSearchResultEvent({ searchTermTyped: state.searchTerm })
      return
    }
    trackSearchSentEvent({
      searchTerm: state.searchTerm,
      totalSuggestions: state.suggestions.length,
    })
  }

  return (
    <div className="relative mb-12 sm:mt-4 lg:mt-12" ref={searchBarRef}>
      <form
        action={EPathSegment.SEARCH}
        onSubmit={handleOnSubmitSearch}
        className="flex items-center py-8 bg-lightgrey-light"
      >
        <input
          type="search"
          placeholder="Suchbegriff, Bestellnummer"
          name="q"
          onChange={(e) => handleOnChange(e.target.value)}
          onKeyDown={handleKeyDownEvent}
          autoComplete="off"
          className="flex-grow text-14 bg-transparent outline-none px-16 placeholder-dark"
        />
        <button type="submit" className="flex border-l-2 border-lightgrey px-12 sm:px-48 lg:px-60">
          <Icon id={EIconId.MAGNIFIER} className="w-24" />
          <span className="font-bold ml-4 sm:ml-12">Suchen</span>
        </button>
      </form>

      {state.suggestions.length > 0 && (
        <ul className="absolute left-0 w-full bg-white text-14 uif-shadow" style={{ zIndex: 110 }}>
          {state.suggestions.map((suggestion: TSuggestElement, index: number) => {
            const chunks = suggestion.name.split(new RegExp(`(${state.searchTerm})`, 'gi'))
            return (
              <li
                key={suggestion.name}
                ref={(ref) => ref && (suggestionRefs[index] = ref)}
                onClick={() => handleOnClickSuggestion(suggestion)}
                className={cx('hover:bg-lightgrey-light transition-color duration-150', {
                  'bg-lightgrey-light': index === state.selectedSuggestion,
                })}
              >
                <a href={`${buildUrlFromSuggestion(suggestion)}`} className="block px-16 py-8">
                  {chunks.map((chunk: string, index: number) => (
                    <span
                      key={index}
                      className={cx({
                        'font-bold': chunk.toLowerCase() === state.searchTerm.toLowerCase(),
                      })}
                    >
                      {chunk}
                    </span>
                  ))}
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default SearchBar
